import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { CrossJoinIntegraton } from '@atlassian/cross-join-nudges';

export type EVENT_TYPE = 'sendTrackEvent' | 'sendOperationalEvent' | 'sendUIEvent';

export const flagKey = 'cross_join_nudges_confluence_to_jpd_experiment';

type FireExperimentAnalytics = (
	type: EVENT_TYPE,
	actionSubject: string,
	action: string,
	actionSubjectId?: string,
	attributes?: Record<string, any>,
) => unknown;

export const getExperimentAnalytics =
	(
		integration: CrossJoinIntegraton,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		{ attributes: commonAttributes = {} } = {},
	): FireExperimentAnalytics =>
	(type, actionSubject, action, actionSubjectId, attributes) =>
		createAnalyticsEvent({
			type,
			data: {
				action,
				actionSubject,
				actionSubjectId,
				source: integration,
				attributes: {
					...commonAttributes,
					...attributes,
				},
			},
		}).fire();

export const fireExperimentError = (analytics: FireExperimentAnalytics, error: Error) =>
	analytics('sendOperationalEvent', 'showCrossJoinNudges', 'failed', undefined, {
		message: String(error),
		details: { ...(error || {}) },
	});

export const fireFlagDismissed = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'flag', 'dismissed', 'requestAccessFlag');

export const fireFlagSnoozed = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'flag', 'snoozed', 'requestAccessFlag');

export const fireUserOptedOut = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'user', 'optedOut', 'requestAccessFlag');

export const fireFlagShown = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'flag', 'shown', 'requestAccessFlag');

export const fireJoinButtonClicked = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'joinButton', 'clicked', 'requestAccessFlag');

export const fireSiteOptedOutAnalytics = (analytics: FireExperimentAnalytics) => {
	analytics('sendTrackEvent', 'eligibility', 'checked', undefined, {
		flagKey: 'NON_CONTEXTUAL_CROSS_JOIN_NUDGES',
		ineligibilityReason: 'SITE_OPTED_OUT',
	});
};

export const getCustomExperimentAnalytics =
	(
		source: string,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		{ attributes: commonAttributes = {} } = {},
	): FireExperimentAnalytics =>
	(type, actionSubject, action, actionSubjectId, attributes) =>
		createAnalyticsEvent({
			type,
			data: {
				action,
				actionSubject,
				actionSubjectId,
				source,
				attributes: {
					...commonAttributes,
					...attributes,
				},
			},
		}).fire();

export const fireCustomExperimentError = (
	analytics: FireExperimentAnalytics,
	error: Error | unknown,
	actionSubject: string,
	action: string,
	analyticsAttributes: Record<string, any>,
) =>
	analytics('sendOperationalEvent', actionSubject, action, undefined, {
		message: String(error),
		details: { ...(error || {}) },
		...analyticsAttributes,
	});

export const fireMsrCjnEligibilityBlocked = (analytics: FireExperimentAnalytics, reason: string) =>
	analytics('sendTrackEvent', 'msrCjnEligibility', 'blocked', undefined, {
		reason,
	});

export const fireMsrCjnEligibilityChecked = (
	analytics: FireExperimentAnalytics,
	attributes: Record<string, any>,
) => analytics('sendTrackEvent', 'msrCjnEligibility', 'checked', undefined, attributes);

// TODO - Refactor this into the a single eligibility checked event in productionisation.
export const fireLoomCjnEligibilityChecked = (
	analytics: FireExperimentAnalytics,
	attributes: Record<string, any>,
) => analytics('sendTrackEvent', 'loomCjnEligibility', 'checked', undefined, attributes);
